import * as THREE from 'three';
import { brushAabb, brushHeightAt, type Aabb, type Brush } from './Brush';

export interface CapsuleBody {
  /** Feet position (bottom of capsule) */
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  radius: number;
  /** Total capsule height including hemispheres */
  height: number;
  grounded: boolean;
  groundNormal: THREE.Vector3;
}

export interface CapsuleWorldConfig {
  gravity: number;
  maxSlopeDeg: number;
  stepHeight: number;
  skin: number;
}

const DEFAULT_CONFIG: CapsuleWorldConfig = {
  gravity: 28,
  maxSlopeDeg: 50,
  stepHeight: 0.4,
  skin: 0.02,
};

/**
 * Capsule vs brush world collision with Quake-style step-up.
 */
export class CapsuleWorld {
  brushes: Brush[] = [];
  config: CapsuleWorldConfig;

  private readonly tmpAabb: Aabb = { min: new THREE.Vector3(), max: new THREE.Vector3() };

  constructor(config: Partial<CapsuleWorldConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  setBrushes(brushes: Brush[]): void {
    this.brushes = brushes.filter((b) => !b.noCollision);
  }

  /**
   * True if a capsule at the body's feet position can exist at `height`
   * without intersecting solid brushes (used for crouch stand-up / headroom).
   */
  canFitHeight(body: CapsuleBody, height: number): boolean {
    const prev = body.height;
    body.height = height;
    const ok = !this.penetrates(body, this.config.skin);
    body.height = prev;
    return ok;
  }

  move(body: CapsuleBody, dt: number): void {
    const { gravity, skin } = this.config;
    this.unstick(body, skin);

    if (!body.grounded) {
      body.velocity.y -= gravity * dt;
    } else if (body.velocity.y < 0) {
      body.velocity.y = 0;
    }

    const dx = body.velocity.x * dt;
    const dy = body.velocity.y * dt;
    const dz = body.velocity.z * dt;

    const startX = body.position.x;
    const startY = body.position.y;
    const startZ = body.position.z;
    const savedVx = body.velocity.x;
    const savedVz = body.velocity.z;

    // Horizontal: try direct, else step-up, else axis slide
    if (Math.abs(dx) > 1e-9 || Math.abs(dz) > 1e-9) {
      if (!this.moveDelta(body, dx, 0, dz, skin)) {
        body.velocity.x = savedVx;
        body.velocity.z = savedVz;

        if (!this.tryStepUp(body, startX, startY, startZ, dx, dz, skin)) {
          body.position.set(startX, startY, startZ);
          body.velocity.x = savedVx;
          body.velocity.z = savedVz;

          if (!this.moveDelta(body, dx, 0, 0, skin)) {
            body.velocity.x = 0;
          }
          if (!this.moveDelta(body, 0, 0, dz, skin)) {
            body.velocity.z = 0;
          }
        } else {
          // Stepped — keep horizontal speed
          body.velocity.x = savedVx;
          body.velocity.z = savedVz;
        }
      }
    }

    // Vertical
    if (Math.abs(dy) > 1e-9) {
      if (!this.moveDelta(body, 0, dy, 0, skin)) {
        body.velocity.y = 0;
      }
    }

    this.updateGrounded(body, skin);

    if (body.grounded && body.velocity.y < 0) {
      body.velocity.y = 0;
    }
  }

  /**
   * Attempt to apply a delta. Returns false if blocked (position left at last free pose).
   * Does NOT modify velocity.
   */
  private moveDelta(body: CapsuleBody, dx: number, dy: number, dz: number, skin: number): boolean {
    if (Math.abs(dx) < 1e-9 && Math.abs(dy) < 1e-9 && Math.abs(dz) < 1e-9) return true;

    const ox = body.position.x;
    const oy = body.position.y;
    const oz = body.position.z;

    body.position.x += dx;
    body.position.y += dy;
    body.position.z += dz;

    if (!this.penetrates(body, skin)) return true;

    let loX = ox;
    let loY = oy;
    let loZ = oz;
    let hiX = body.position.x;
    let hiY = body.position.y;
    let hiZ = body.position.z;

    for (let i = 0; i < 10; i++) {
      const mx = (loX + hiX) * 0.5;
      const my = (loY + hiY) * 0.5;
      const mz = (loZ + hiZ) * 0.5;
      body.position.set(mx, my, mz);
      if (this.penetrates(body, skin)) {
        hiX = mx;
        hiY = my;
        hiZ = mz;
      } else {
        loX = mx;
        loY = my;
        loZ = mz;
      }
    }

    body.position.set(loX, loY, loZ);
    return false;
  }

  private tryStepUp(
    body: CapsuleBody,
    startX: number,
    startY: number,
    startZ: number,
    dx: number,
    dz: number,
    skin: number,
  ): boolean {
    const step = this.config.stepHeight;

    // Up
    body.position.set(startX, startY + step, startZ);
    if (this.penetrates(body, skin)) {
      body.position.set(startX, startY, startZ);
      return false;
    }

    // Forward while elevated — allow partial
    this.moveDelta(body, dx, 0, dz, skin);

    // Also nudge forward up to almost a radius so low ledges clear in one attempt
    const fwdLen = Math.hypot(dx, dz);
    if (fwdLen > 1e-8) {
      const nx = dx / fwdLen;
      const nz = dz / fwdLen;
      const assist = Math.min(body.radius * 0.9, step);
      this.moveDelta(body, nx * assist, 0, nz * assist, skin);
    }

    if (Math.hypot(body.position.x - startX, body.position.z - startZ) < 1e-4) {
      body.position.set(startX, startY, startZ);
      return false;
    }

    // Find ground under new position and snap if within step range
    const support = this.findSupport(
      body.position.x,
      body.position.y + 0.05,
      body.position.z,
      body.radius,
    );

    if (support.height === -Infinity || !this.isWalkable(support.normal)) {
      body.position.set(startX, startY, startZ);
      return false;
    }

    const climb = support.height - startY;
    if (climb < -0.05 || climb > step + skin) {
      body.position.set(startX, startY, startZ);
      return false;
    }

    // Tentatively land
    const landX = body.position.x;
    const landZ = body.position.z;
    body.position.set(landX, support.height, landZ);

    if (this.penetrates(body, skin)) {
      body.position.set(startX, startY, startZ);
      return false;
    }

    body.grounded = true;
    body.groundNormal.copy(support.normal);
    body.velocity.y = 0;
    return true;
  }

  private updateGrounded(body: CapsuleBody, skin: number): void {
    body.grounded = false;
    body.groundNormal.set(0, 1, 0);

    // Probe at least a step up so walking into a slope can lift onto it
    const probeDist = this.config.stepHeight + skin;
    const support = this.findSupport(body.position.x, body.position.y + probeDist, body.position.z, body.radius);
    if (support.height === -Infinity) return;

    const gap = body.position.y - support.height;
    // gap > 0: floating above surface; gap < 0: feet below walkable surface (slope climb)
    if (gap <= probeDist && gap >= -probeDist && body.velocity.y <= 0.75 && this.isWalkable(support.normal)) {
      const prevY = body.position.y;
      body.position.y = support.height;
      if (this.penetrates(body, skin)) {
        body.position.y = prevY;
        return;
      }
      body.grounded = true;
      body.groundNormal.copy(support.normal);
      if (body.velocity.y < 0) body.velocity.y = 0;
    }
  }

  /**
   * Push out of a door leaf that just became solid. Tries both faces (and the
   * long edges) and keeps the pose that is actually free — so an open leaf
   * against a wall shoves you into the room, not into the jamb.
   */
  unstick(body: CapsuleBody, skin = this.config.skin, iterations = 5): void {
    const r = body.radius - skin;
    if (r <= 0.05) return;
    const pad = r + skin + 0.02;
    for (let i = 0; i < iterations; i++) {
      if (!this.penetrates(body, skin)) return;
      const bottomY = body.position.y + body.radius;
      const topY = body.position.y + body.height - body.radius;
      let pushed = false;
      for (const brush of this.brushes) {
        if (brush.noCollision || !brush.id.startsWith('door_leaf')) continue;
        if (!this.penetratesBox(body.position.x, body.position.z, bottomY, topY, r, brush)) continue;
        if (this.pushOutOfDoor(body, brush, pad, skin)) {
          pushed = true;
        } else {
          // Wedged (leaf + jamb). Ghost the leaf so swept movement can walk out.
          brush.noCollision = true;
        }
      }
      if (!pushed) return;
    }
  }

  private pushOutOfDoor(body: CapsuleBody, brush: Brush, pad: number, skin: number): boolean {
    const hx = brush.size.x * 0.5;
    const hz = brush.size.z * 0.5;
    const dx = body.position.x - brush.center.x;
    const dz = body.position.z - brush.center.z;
    const c = Math.cos(brush.yaw);
    const s = Math.sin(brush.yaw);
    const lx = c * dx - s * dz;
    const lz = s * dx + c * dz;

    const ox = body.position.x;
    const oz = body.position.z;
    const thinIsZ = hz <= hx;
    const candidates = [
      { dlx: 0, dlz: hz + pad - lz, thin: thinIsZ },
      { dlx: 0, dlz: -(hz + pad) - lz, thin: thinIsZ },
      { dlx: hx + pad - lx, dlz: 0, thin: !thinIsZ },
      { dlx: -(hx + pad) - lx, dlz: 0, thin: !thinIsZ },
    ];
    candidates.sort((a, b) => {
      if (a.thin !== b.thin) return a.thin ? -1 : 1;
      return Math.hypot(a.dlx, a.dlz) - Math.hypot(b.dlx, b.dlz);
    });

    for (const cand of candidates) {
      body.position.x = ox + c * cand.dlx + s * cand.dlz;
      body.position.z = oz - s * cand.dlx + c * cand.dlz;
      if (!this.penetrates(body, skin)) return true;
    }

    body.position.x = ox;
    body.position.z = oz;
    return false;
  }

  private penetrates(body: CapsuleBody, skin: number): boolean {
    const r = body.radius - skin;
    if (r <= 0.05) return false;

    const bottomY = body.position.y + body.radius;
    const topY = body.position.y + body.height - body.radius;
    const x = body.position.x;
    const z = body.position.z;

    for (const brush of this.brushes) {
      if (brush.noCollision) continue;
      if (brush.kind === 'ramp') {
        if (this.penetratesRamp(x, z, bottomY, topY, r, body.position.y, brush)) return true;
      } else if (this.penetratesBox(x, z, bottomY, topY, r, brush)) {
        return true;
      }
    }
    return false;
  }

  private penetratesBox(
    x: number,
    z: number,
    bottomY: number,
    topY: number,
    radius: number,
    brush: Brush,
  ): boolean {
    const aabb = brushAabb(brush, this.tmpAabb);
    const distSq = distSqSegmentAabb(x, bottomY, z, topY, aabb);
    return distSq < radius * radius;
  }

  private penetratesRamp(
    x: number,
    z: number,
    bottomY: number,
    topY: number,
    radius: number,
    feetY: number,
    brush: Brush,
  ): boolean {
    const aabb = brushAabb(brush, this.tmpAabb);
    const margin = radius;
    if (
      x < aabb.min.x - margin ||
      x > aabb.max.x + margin ||
      z < aabb.min.z - margin ||
      z > aabb.max.z + margin
    ) {
      return false;
    }

    // Only the volume deeper than a step under the slope is solid.
    // Shallower penetration is a walkable rise — grounded snap lifts the capsule.
    const solidDepth = this.config.stepHeight;

    const samples: [number, number][] = [
      [0, 0],
      [radius * 0.7, 0],
      [-radius * 0.7, 0],
      [0, radius * 0.7],
      [0, -radius * 0.7],
    ];

    for (const [ox, oz] of samples) {
      const sx = x + ox;
      const sz = z + oz;
      if (sx < aabb.min.x || sx > aabb.max.x || sz < aabb.min.z || sz > aabb.max.z) continue;
      const h = brushHeightAt(brush, sx, sz);
      if (h === -Infinity) continue;
      const below = h - feetY;
      if (below > solidDepth && topY + radius > aabb.min.y && bottomY < h + radius) {
        return true;
      }
    }
    return false;
  }

  private findSupport(
    x: number,
    fromY: number,
    z: number,
    radius: number,
  ): { height: number; normal: THREE.Vector3 } {
    let best = -Infinity;
    const bestNormal = new THREE.Vector3(0, 1, 0);

    const offsets: [number, number][] = [
      [0, 0],
      [radius * 0.45, 0],
      [-radius * 0.45, 0],
      [0, radius * 0.45],
      [0, -radius * 0.45],
    ];

    for (const brush of this.brushes) {
      if (brush.noCollision) continue;
      for (const [ox, oz] of offsets) {
        const h = brushHeightAt(brush, x + ox, z + oz);
        if (h === -Infinity || h > fromY + 0.02 || h <= best) continue;
        best = h;
        if (brush.kind === 'ramp') {
          // Tip toward local −X, transformed with Three.js rotation.y
          const pitch = Math.atan2(brush.size.y, brush.size.x);
          const sp = Math.sin(pitch);
          const c = Math.cos(brush.yaw);
          const s = Math.sin(brush.yaw);
          bestNormal.set(-sp * c, Math.cos(pitch), sp * s);
          bestNormal.normalize();
        } else {
          bestNormal.set(0, 1, 0);
        }
      }
    }

    return { height: best, normal: bestNormal.clone() };
  }

  private isWalkable(n: THREE.Vector3): boolean {
    const maxSlope = this.config.maxSlopeDeg * (Math.PI / 180);
    const angle = Math.acos(clamp(n.y, -1, 1));
    return angle <= maxSlope + 1e-3;
  }
}

function distSqSegmentAabb(
  x: number,
  bottomY: number,
  z: number,
  topY: number,
  aabb: Aabb,
): number {
  const segMinY = Math.min(bottomY, topY);
  const segMaxY = Math.max(bottomY, topY);

  const closestX = clamp(x, aabb.min.x, aabb.max.x);
  const closestZ = clamp(z, aabb.min.z, aabb.max.z);

  let closestY: number;
  if (segMaxY < aabb.min.y) closestY = aabb.min.y;
  else if (segMinY > aabb.max.y) closestY = aabb.max.y;
  else closestY = clamp((segMinY + segMaxY) * 0.5, aabb.min.y, aabb.max.y);

  const cy = clamp(closestY, segMinY, segMaxY);
  const dx = x - closestX;
  const dy = cy - closestY;
  const dz = z - closestZ;
  return dx * dx + dy * dy + dz * dz;
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}
